import { Component, OnInit } from '@angular/core';
import { Observable, of } from 'rxjs';
import { take, catchError } from 'rxjs/operators';
import { Product } from 'src/app/order/models/product.model';
import { ProductService } from 'src/app/quoting/services/product.service';
import { AuthService } from 'src/app/auth/services/auth.service';
import { GridColumn } from 'src/app/core/models/grid-column.model';
import { GridComponent } from 'src/app/core/widgets/grid/grid.component';
import { Warehouse } from '../../models/warehouse.model';
import { WarehouseLocation } from '../../models/warehouse-location.model';
import { WarehouseService } from '../../services/warehouse.service';
import { WarehouseLocationService } from '../../services/warehouse-location.service';
import { StockQuantityService } from '../../services/stock-quantity.service';
import { StockMovementReferenceService } from '../../services/stock-movement-reference.service';
import { WarehouseReportingService } from '../../services/warehouse-reporting.service';
import { ProductStockMovementReference } from '../../models/product-stock-movement-reference.model';
import { StockOnHandResult, StockRow } from '../../models/warehouse-reporting.model';
import { CostDisplayService } from '../cost-history/cost-display.service';
import { WarehouseLocationCodeService } from '../../services/warehouse-location-code.service';
// Shared status token rather than a second copy of the hex, and never the only signal - see the
// write-down cell in the template.
import { STATUS_CRITICAL } from '../../services/warehousing-chart.service';
import { StockRevaluation, StockRevaluationLine } from './stock-revaluation.model';
import { StockRevaluationService } from './stock-revaluation.service';
import { StockRevaluationValidatorService } from './stock-revaluation-validator.service';
import { StockHoldingService } from './stock-holding.service';

/** The route as the frontend guard names it, which is what a permission's components list holds. */
export const REVALUATION_ROUTE = '/warehousing/stock-revaluation';

@Component({
    selector: 'app-stock-revaluation',
    templateUrl: './stock-revaluation.component.html',
    styleUrls: ['./stock-revaluation.component.scss'],
    standalone: false
})
export class StockRevaluationComponent implements OnInit {

  revaluation: StockRevaluation = new StockRevaluation();

  warehouses: Warehouse[] = [];
  products: Product[] = [];
  references: ProductStockMovementReference[] = [];
  locations: WarehouseLocation[] = [];

  /** What the operator typed into the location box. Transient, and deliberately not on the line. */
  locationText: string = '';

  // The same aisle and shelf can exist at two sites, so a location is only ever picked through the
  // warehouse that scopes it.
  warehouseId: number = -1;

  newLine: StockRevaluationLine = new StockRevaluationLine();

  /**
   * The whole holding behind the product picked on the new line: every location it sits in, at
   * every site, with the money and the coverage classification the reporting endpoint answers.
   *
   * Fetched product-wide and deliberately NOT scoped by the warehouse selected above. That is the
   * entire reason it is on this screen. The value an operator states applies to every unit of the
   * product anywhere, so somebody shown only the shelf they happened to inspect would read the
   * figure as belonging to that shelf and wipe the value of stock at another site. Showing the
   * whole holding is what makes that misreading impossible rather than merely discouraged.
   *
   * Null while no product is chosen and while a fetch is in flight, which destroys the panel rather
   * than leaving it stale: the previous product's locations must not sit under the new product's
   * name for the length of a request.
   */
  holding: StockOnHandResult = null;
  loadingHolding: boolean = false;

  /**
   * The holding each added line was valued against, keyed by product id and captured at the moment
   * the line was added.
   *
   * A stated value says nothing on its own about how much money a line moves - that is the stated
   * value minus what the product is worth now - so the comparison is kept per line rather than
   * re-derived at render time from whatever the panel happens to be showing.
   */
  holdings: { [productId: number]: StockOnHandResult } = {};

  /**
   * Columns for the holding panel.
   *
   * Every cell answers a string. The grid blanks any falsy value it is handed
   * (`grid.component.ts:385`), so a location holding zero units, or worth £0.00, would render as an
   * empty cell - and on this screen, where zero is now a legitimate thing to state, an empty cell
   * where a zero belongs is the precise confusion the change is about.
   */
  holdingColumns: GridColumn[] = [
    {active: true, label: 'Warehouse', field: 'warehouseName'} as GridColumn,
    {active: true, label: 'Aisle', field: 'aisle'} as GridColumn,
    {active: true, label: 'Shelf', field: 'shelf'} as GridColumn,
    {active: true, label: 'Qty on hand', field: 'ledgerQty',
      value: (v: any) => this.stockQuantityService.round(v).toString()} as GridColumn,
    {active: true, label: 'Value at cost', field: 'value',
      value: (v: any, row: StockRow) => this.stockHolding.rowValue(row)} as GridColumn
  ];

  /** Reserved status colour, used only alongside an arrow and the word "write-down". */
  statusCritical: string = STATUS_CRITICAL;

  isError: boolean = false;
  isServerError: boolean = false;
  isForbidden: boolean = false;
  serverErrorMessage: string = '';

  constructor(private warehouseService: WarehouseService,
    private warehouseLocationService: WarehouseLocationService,
    private productService: ProductService,
    private stockMovementReferenceService: StockMovementReferenceService,
    private warehouseReportingService: WarehouseReportingService,
    private stockQuantityService: StockQuantityService,
    private stockRevaluationService: StockRevaluationService,
    private stockRevaluationValidator: StockRevaluationValidatorService,
    private stockHolding: StockHoldingService,
    private costDisplay: CostDisplayService,
    private authService: AuthService,
    private locationCodes: WarehouseLocationCodeService) { }

  ngOnInit(): void {
    this.loadWarehouses();
    this.loadProducts();
    this.loadReferences();
  }

  /**
   * Whether this user is granted the revaluation route.
   *
   * Worth being plain about what this does and does not do. It hides a control the user cannot use,
   * which is a courtesy, not a gate: the backend's own permission check returns true unconditionally
   * with its body commented out, and it is the only enforcement layer, so the route is reachable by
   * any authenticated caller regardless of what this answers. Separately, no permission row grants
   * any /warehousing route today, so in practice only God Mode answers true here. Treat this as a
   * label until the backend check is switched on.
   */
  canRevalue(): boolean {
    return this.authService.checkIfUserCanAccessRoute(REVALUATION_ROUTE);
  }

  loadWarehouses() {
    this.warehouseService.getAllWarehouses()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert("Cannot load warehouses");
      return e;
    }))
    .subscribe((data: Warehouse[]) => {
      this.warehouses = data;
    })
  }

  loadProducts() {
    this.productService.getAllActiveProducts()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert("Cannot load products");
      return e;
    }))
    .subscribe((data: Product[]) => {
      this.products = data;
    })
  }

  loadReferences() {
    this.stockMovementReferenceService.getAllStockMovementReferences()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert("Cannot load movement references");
      return e;
    }))
    .subscribe((data: ProductStockMovementReference[]) => {
      this.references = data;
    })
  }

  onReferenceChanged(referenceId: number) {
    this.revaluation.referenceId = +referenceId;
  }

  /**
   * Changing the warehouse invalidates every location already picked under it, so the lines go with
   * it rather than being recorded against the wrong site.
   */
  onWarehouseChanged(warehouseId: number) {
    this.warehouseId = +warehouseId;
    this.locations = [];
    this.newLine.warehouseLocationId = -1;
    // Cleared with the id, or a code naming a shelf at the site just left would read as accepted.
    this.locationText = '';

    this.clearLines();

    if (this.warehouseId !== -1) {
      this.loadLocations();
    }
  }

  loadLocations() {
    this.warehouseLocationService.getWarehouseLocationsForWarehouse(this.warehouseId)
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert("Cannot load warehouse locations");
      return e;
    }))
    .subscribe((data: WarehouseLocation[]) => {
      this.locations = data;
    })
  }

  /**
   * Picking the product is what settles which holding is being valued, so the whole holding is
   * fetched the moment one is chosen rather than once the line has been added. By then it is too
   * late to be told what the figure will be measured against.
   */
  onNewLineProductChanged(productId: number) {
    this.newLine.productId = +productId;

    this.holding = null;

    if (this.newLine.productId === -1) {
      this.loadingHolding = false;
      return;
    }

    this.loadHolding(this.newLine.productId);
  }

  /**
   * Product-wide, and deliberately not scoped by the warehouse selected above.
   *
   * Sending the warehouse would show the operator a part of the stock they are about to put a value
   * on, which is exactly the misreading this panel exists to close: the value applies to every unit
   * of the product, at every site, whatever location the line happens to record.
   */
  loadHolding(productId: number) {
    this.loadingHolding = true;

    this.warehouseReportingService.getStockOnHand({productId: productId})
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      this.loadingHolding = false;
      alert("Cannot load the product's stock holding");
      return e;
    }))
    .subscribe((data: StockOnHandResult) => {
      // A slow first request can land after the operator has moved to another product, and the
      // panel would then show one product's locations and money under another product's name.
      if (this.newLine.productId !== productId) {
        return;
      }

      this.holding = data ? data : new StockOnHandResult();
      this.loadingHolding = false;
    })
  }

  /**
   * Feeds the holding panel from the result already in hand rather than letting the grid query an
   * entity of its own.
   *
   * It has to come from here: `product_stock_level` is a cache of the ledger and the two can
   * disagree, so a grid pointed at that entity would show the number reconciliation exists to
   * distrust, right beside a money figure replayed from the ledger.
   */
  getHoldingRecords(grid: GridComponent): Observable<any> {
    const rows: StockRow[] = this.stockHolding.rows(this.holding);
    const from: number = (grid.newPage - 1) * grid.amount;
    const page: StockRow[] = rows.slice(from, from + grid.amount);

    return of({data: page, total: rows.length, count: page.length});
  }

  /**
   * Money is a float, so it is rounded on the way in rather than at comparison time, and on blur
   * rather than on each keystroke so a part-typed decimal is left alone.
   *
   * An emptied field is put back to null rather than rounded. `round()` answers 0 for anything it
   * cannot use, which was harmless while 0 was refused and is not now: it would turn a field the
   * operator cleared into a statement that the holding is worth nothing.
   */
  onTargetValueChanged(line: StockRevaluationLine) {
    if (!this.isTargetValueSupplied(line)) {
      line.targetValue = null;
      return;
    }

    line.targetValue = this.stockQuantityService.round(line.targetValue);
  }

  /** Whether an amount was stated at all, which is a different question from what it is. */
  isTargetValueSupplied(line: StockRevaluationLine): boolean {
    return line.targetValue !== null && line.targetValue !== undefined
      && String(line.targetValue) !== ''
      && Number.isFinite(Number(line.targetValue));
  }

  /**
   * Negative is the only refusal, and the rule this replaces said the opposite.
   *
   * A signed delta of zero moved no money and so was not a correction. That was true of a delta and
   * is false of a stated value: a holding worth nothing is a real answer, and writing stock off
   * entirely is the correction an operator most needs to be able to state. Negative goes the other
   * way - stock cannot be worth less than nothing - and the backend refuses it as well.
   */
  isTargetValueInvalid(line: StockRevaluationLine): boolean {
    return !this.isTargetValueSupplied(line)
      || this.stockQuantityService.isNegative(line.targetValue);
  }

  addLine() {
    this.onTargetValueChanged(this.newLine);

    if (this.newLine.productId === -1 || this.newLine.warehouseLocationId === -1) {
      alert('Please select a product and a location for the line');
      return;
    }

    if (!this.isTargetValueSupplied(this.newLine)) {
      alert('Enter what this product\'s whole holding is worth. Enter 0 to write it off entirely.');
      return;
    }

    if (this.isTargetValueInvalid(this.newLine)) {
      alert('A stated value must not be negative. Stock cannot be worth less than nothing - enter '
        + '0 to write the holding off entirely.');
      return;
    }

    // The value is product-wide, so two lines naming one product state two different values for the
    // same holding and the later one would simply win. Two locations of the same product is this
    // case rather than an exception to it. The backend refuses it as well; caught here so the
    // operator finds out while they can still see what they picked.
    if (this.hasLineForProduct(this.newLine.productId)) {
      alert('This product is already on the revaluation. A stated value covers the whole holding, '
        + 'so a product can only be valued once - including across two locations.');
      return;
    }

    // Captured as the line is added, so the money the line moves stays measurable against the
    // holding it was actually judged against.
    this.holdings[this.newLine.productId] = this.holding;

    this.revaluation.lines.push(JSON.parse(JSON.stringify(this.newLine)));

    this.newLine = new StockRevaluationLine();
    this.locationText = '';
    this.holding = null;
  }

  hasLineForProduct(productId: number): boolean {
    return this.revaluation.lines.filter(line => line.productId === productId).length > 0;
  }

  deleteLine(index: number) {
    this.revaluation.lines.splice(index, 1);
  }

  getProductName(productId: number): string {
    const product = this.products.filter(p => p.id === productId)[0];

    return product ? product.name : '';
  }

  getLocationName(locationId: number): string {
    const location = this.locations.filter(l => l.id === locationId)[0];

    return location ? location.aisle + ' / ' + location.shelf : '';
  }

  /**
   * The location is typed here as it is on the operation form, and resolved by the same service so
   * that one code means one shelf on every screen that takes one.
   *
   * Typing it costs nothing here that it does not cost there, and this screen has a reason of its own
   * to prefer it: the holding panel above lists every location the product sits in, so the operator
   * is reading a code off that panel and copying it into the line. Copying is a keyboard action.
   *
   * Nothing about the money moves with this. The line's location records where the correction was
   * entered against and scopes nothing, exactly as the copy below the picker says - resolving it from
   * text rather than a list does not change what it means.
   */
  onLocationTextChanged(text: string) {
    this.locationText = text;
    this.newLine.warehouseLocationId = this.locationCodes.resolve(text, this.locations);
  }

  /** What the typed code resolved to, blank until it resolves. */
  resolvedLocationLabel(): string {
    return this.newLine.warehouseLocationId === -1
      ? ''
      : this.getLocationName(this.newLine.warehouseLocationId);
  }

  /** Why a typed code did not resolve, blank when it did or when nothing has been typed. */
  locationError(): string {
    return this.locationCodes.error(this.locationText, this.locations);
  }

  /**
   * The panel above the lines, reading the holding for the product on the new line.
   *
   * These are the figures the operator is being asked to replace, so they are read straight off the
   * ledger replay and never re-derived here.
   */
  getHoldingLocationCount(): number {
    return this.stockHolding.rows(this.holding).length;
  }

  getHoldingQty(): string {
    return this.stockHolding.qty(this.holding);
  }

  getHoldingValue(): string {
    return this.stockHolding.value(this.holding);
  }

  /** The gate on the one reassuring sentence on this screen. See the template. */
  doesHoldingValueCoverEverything(): boolean {
    return this.stockHolding.coversEverything(this.holding);
  }

  isHoldingValueIncomplete(): boolean {
    return this.stockHolding.isValueIncomplete(this.holding);
  }

  /** Both of these are refused by the backend, so they are named before the operator posts. */
  doesHoldingHoldNoStock(): boolean {
    return this.holding !== null && this.stockHolding.holdsNoStock(this.holding);
  }

  doesHoldingHaveNoCostBasis(): boolean {
    return this.stockHolding.hasNoCostBasis(this.holding);
  }

  /** Formatted as a plain total and never signed: a stated value is a figure, not a movement. */
  getFormattedTargetValue(value: number): string {
    return this.costDisplay.cost(value);
  }

  /**
   * The whole holding a line was valued against, as it stood when the line was added.
   *
   * Falls through to the live panel for the line still being typed, which is not in `holdings` yet
   * - it is captured on add. Both answer the same object, and that is the point: the movement
   * previewed beside the input is the one the added line will go on reporting.
   */
  getLineHolding(line: StockRevaluationLine): StockOnHandResult {
    if (this.holdings[line.productId] !== undefined) {
      return this.holdings[line.productId];
    }

    return line.productId === this.newLine.productId ? this.holding : null;
  }

  getLineCurrentValue(line: StockRevaluationLine): string {
    const holding = this.getLineHolding(line);

    // Only reachable when the holding lookup failed, which alerts at the time. Named rather than
    // left to render as "Not priced": nobody here knows what the stock is worth, which is a
    // different fact from nobody ever having priced it.
    return holding ? this.stockHolding.value(holding) : 'Not known';
  }

  /**
   * Whether the money this line moves can be stated at all.
   *
   * It is the stated value minus the current one, so it is only as good as the current one. Where
   * the valuation omits units - no cost basis, a negative average, intake that carried no cost -
   * the endpoint still answers a number, and subtracting from it would print a confident movement
   * derived from a figure that covers part of the stock. The line says so instead.
   */
  isLineChangeKnown(line: StockRevaluationLine): boolean {
    return this.stockHolding.coversEverything(this.getLineHolding(line));
  }

  getLineChange(line: StockRevaluationLine): string {
    return this.stockHolding.formattedChange(this.getLineHolding(line), line.targetValue);
  }

  isLineWriteDown(line: StockRevaluationLine): boolean {
    return this.isLineChangeKnown(line)
      && this.stockHolding.isWriteDown(this.getLineHolding(line), line.targetValue);
  }

  /**
   * What the listed holdings will be worth once this is posted.
   *
   * This is NOT the old net figure renamed, and must not be read as one. Signed deltas added up to
   * the money a batch moved. Stated values add up to what the products will be worth, which is a
   * different quantity and is not a movement at all - the money moved is reported separately below
   * it, and only when it can be. One line per product is what makes even this addition legitimate:
   * the same holding cannot be counted twice.
   */
  getTotalStatedValue(): string {
    let total = 0;

    this.revaluation.lines.forEach((line) => {
      total += Number(line.targetValue) || 0;
    });

    return this.costDisplay.cost(total);
  }

  /** Lines whose product's current value covers part of its stock, so no movement follows from it. */
  getUnknownChangeCount(): number {
    return this.revaluation.lines.filter(line => !this.isLineChangeKnown(line)).length;
  }

  /**
   * Whether the batch total means anything.
   *
   * All or nothing on purpose. A total mixing real differences with lines measured against a value
   * that omits half their stock would be printed with exactly the same authority as one that adds
   * up, and there is nothing in the figure itself to tell a reader which it is.
   */
  isNetChangeKnown(): boolean {
    return this.revaluation.lines.length > 0 && this.getUnknownChangeCount() === 0;
  }

  /** The money this batch actually moves, against what the products are worth today. */
  getNetChange(): string {
    return this.costDisplay.signed(this.netChange());
  }

  /** Whether the batch as a whole writes value off, so the total can carry the same marking. */
  isNetWriteDown(): boolean {
    return this.stockQuantityService.isNegative(this.netChange());
  }

  /**
   * Summed unrounded and rounded once by the formatter. Rounding each line first and adding those
   * up drifts away from the answer, and on a correction the whole point is that the figure
   * reconciles.
   */
  private netChange(): number {
    let total = 0;

    this.revaluation.lines.forEach((line) => {
      total += this.stockHolding.change(this.getLineHolding(line), line.targetValue);
    });

    return total;
  }

  /**
   * Posts the whole batch in one request, because the backend runs it as one transaction.
   *
   * No quantity is sent and none is collected. The stock levels are untouched by design, so the
   * screen returns to the cost history rather than to the stock levels: sending an operator to a
   * levels screen after this would invite them to look for a change that was never going to be there.
   */
  onSubmit() {

    this.isError = false;
    this.isServerError = false;
    this.isForbidden = false;
    this.serverErrorMessage = '';

    if (this.stockRevaluationValidator.isValid(this.revaluation)) {
      this.stockRevaluationService.revalue(this.revaluation)
      .pipe(take(1))
      .pipe(catchError((e) => {
        if (e.status === 401 || e.status === 403) {
          this.isForbidden = true;
        } else {
          this.isServerError = true;
          this.serverErrorMessage = e.error && e.error.message ? e.error.message : '';
        }
        return e;
      }))
      .subscribe(() => {
        window.location.href = '/warehousing/cost-history';
      })
    } else {
      this.isError = true;
    }
  }

  private clearLines() {
    if (this.revaluation.lines.length === 0) {
      return;
    }

    this.revaluation.lines = [];

    // The captured holdings go with them. They are keyed by product and a stale one would attach
    // itself to the next line naming that product, reporting a movement against a figure read
    // before the operator started over.
    this.holdings = {};

    alert('The lines have been cleared because their locations belonged to the warehouse you changed');
  }
}
